import { mapColorOf, withAlpha } from '../brand/mapColors'
import {
  PLATE_LINE_H,
  PLATE_PAD,
  exportPlateLines,
  plateBox,
  plateHeaderLines,
  plateLineColor,
  type ExportContext,
} from './exportPlate'
import type { PlateMark } from '../maps/legendRows'
import type { GraticuleFrame } from './graticule'
import { getLang } from '../i18n'
import { activeBasemap } from './baseStyle'
import { drawAttribution, prepareAttribution } from './attribution/exportAttribution'
import {
  TIMER_BAR_H,
  TIMER_LINE_H,
  TIMER_PAD,
  timerBarMarks,
  timerLabel,
  timerLayout,
  type TimerFrame,
} from './exportTimer'

/**
 * Кадр карты на скачивание: полотно карты + плашка с датой, слоем и глубиной.
 *
 * Отсюда берут кадры оба выхода — PNG (снимок как есть) и запись MP4
 * (см. exportVideo). Снять пиксели можно только при `preserveDrawingBuffer: true`
 * у карты, иначе WebGL очищает буфер до того, как мы до него доберёмся, и
 * приходит пустота.
 */

const PLATE_FONT = '600 12px "IBM Plex Mono", ui-monospace, monospace'

/** Шрифт таймера. Крупнее плашки втрое: дату в ролике читают мельком и часто
 *  с телефона. */
const TIMER_FONT = '700 26px "IBM Plex Mono", ui-monospace, monospace'

/** Поле под цветную метку строки легенды, px. */
const MARK_W = 20

/** Метка легенды: линия рисуется с ореолом — ровно как на карте, иначе тёмная
 *  изотерма на тёмной плашке пропадает.
 *
 *  Экспортируется ради тестов: как метка легла на пиксели, не проверить, а вот
 *  что пунктир нарисован пунктиром и полая точка не залита — обязано держаться.
 */
export function drawMark(
  ctx: CanvasRenderingContext2D,
  mark: PlateMark,
  x: number,
  cy: number,
): void {
  if (mark.kind === 'ramp') {
    const grad = ctx.createLinearGradient(x, cy, x + 14, cy)
    mark.stops.forEach((c, i) => grad.addColorStop(i / Math.max(1, mark.stops.length - 1), c))
    ctx.fillStyle = grad
    ctx.fillRect(x, cy - 2.5, 14, 5)
    return
  }
  if (mark.kind === 'halo') {
    // Бледный круг + точка в центре: ровно как ореол неопределённости на карте.
    ctx.globalAlpha = 0.18
    ctx.fillStyle = mark.color
    ctx.beginPath()
    ctx.arc(x + 7, cy, 5.5, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalAlpha = 0.55
    ctx.strokeStyle = mark.color
    ctx.lineWidth = 1
    ctx.stroke()
    // Прозрачность — состояние холста: не вернув её, мы обесцветили бы всё ниже.
    ctx.globalAlpha = 1
    ctx.fillStyle = mark.color
    ctx.beginPath()
    ctx.arc(x + 7, cy, 1.8, 0, Math.PI * 2)
    ctx.fill()
    return
  }
  if (mark.kind === 'dot') {
    ctx.beginPath()
    ctx.arc(x + 6, cy, 3.5, 0, Math.PI * 2)
    // Полая точка — замёрзший трек: от вылова того же цвета её отличает только
    // пустая середина, залить её значило бы стереть разницу.
    if (!mark.hollow) {
      ctx.fillStyle = mark.color
      ctx.fill()
    }
    if (mark.hollow || mark.ring) {
      ctx.strokeStyle = mark.ring ?? mark.color
      ctx.lineWidth = 1.6
      ctx.stroke()
    }
    return
  }
  if (mark.casing) {
    ctx.strokeStyle = mark.casing
    ctx.lineWidth = 5
    ctx.beginPath()
    ctx.moveTo(x, cy)
    ctx.lineTo(x + 14, cy)
    ctx.stroke()
  }
  ctx.setLineDash(mark.dash ? [4, 3] : [])
  ctx.strokeStyle = mark.color
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(x, cy)
  ctx.lineTo(x + 14, cy)
  ctx.stroke()
  ctx.setLineDash([])
}

/** Шрифт подписей сетки: тот же кегль, что на экране (10 px в CSS). */
const GRID_FONT_PX = 10
const GRID_FONT = (px: number) => `${px}px "IBM Plex Mono", ui-monospace, monospace`

/** Цвета сетки. НЕ тема кабинета: скачанный кадр уходит наружу и обязан
 *  читаться и на светлой суше, и на тёмном море. Подложка подписи — цвет
 *  плашки домена (загрузчик пака требует от него контраста AA с белым),
 *  линия и текст общие: это приёмы читаемости, а не айдентика. */
const GRID_LINE_COLOR = 'rgba(148,163,184,0.45)'
/** Линия без подписи — бледнее в том же отношении, что на экране (25 → 20). */
const GRID_LINE_FAINT = 'rgba(148,163,184,0.36)'
const gridPillColor = () => withAlpha(mapColorOf('export-plate'), 0.72)
const GRID_TEXT_COLOR = 'rgba(226,232,240,0.92)'

/** Поля подписи и высота её подложки, в CSS-пикселях экрана. */
const GRID_LABEL_PAD = 4
const GRID_LABEL_H = 14
/** Отступ подписи от края кадра и от своей линии — как в MapGraticule. */
const GRID_LABEL_GAP = 2
const GRID_LABEL_OFFSET = 3

/** Координатная сетка на готовом кадре: линии по меридианам и параллелям плюс
 *  подписи по четырём краям.
 *
 *  На экране шкала живёт в DOM поверх карты (MapGraticule), поэтому в снимок
 *  WebGL-полотна она не попадает — скачанный кадр приходил без координат.
 *  Здесь тот же снимок делений перекладывается на кадр: `grat.width` — экран в
 *  CSS-пикселях, `width` — кадр, и всё умножается на их отношение. Забудь мы
 *  про него — на retina сетка сбилась бы вдвое и стала бы просто врать.
 */
export function drawGraticule(
  ctx: CanvasRenderingContext2D,
  grat: GraticuleFrame | null | undefined,
  width: number,
  height: number,
): void {
  if (!grat || !(grat.width > 0) || !(grat.height > 0)) return
  const k = width / grat.width
  if (!Number.isFinite(k) || k <= 0) return

  ctx.save()
  // Линия толщиной ровно в пиксель кадра: на retina-кадре волосяная линия
  // пропадает при пережатии в видео.
  const thick = Math.max(1, Math.round(k))
  // Неподписанные линии бледнее — как на экране (MapGraticule): на густой сетке
  // ровный тон читается штриховкой.
  for (const t of grat.lon) {
    ctx.fillStyle = t.labelled ? GRID_LINE_COLOR : GRID_LINE_FAINT
    ctx.fillRect(Math.round(t.px * k), 0, thick, height)
  }
  for (const t of grat.lat) {
    ctx.fillStyle = t.labelled ? GRID_LINE_COLOR : GRID_LINE_FAINT
    ctx.fillRect(0, Math.round(t.px * k), width, thick)
  }

  const font = GRID_FONT_PX * k
  const pad = GRID_LABEL_PAD * k
  const boxH = GRID_LABEL_H * k
  const gap = GRID_LABEL_GAP * k
  ctx.font = GRID_FONT(font)
  ctx.textBaseline = 'top'

  const pill = (text: string, x: number, y: number) => {
    const w = ctx.measureText(text).width + pad * 2
    // Подпись у самого края кадра ушла бы за него — прижимаем внутрь.
    const left = Math.max(0, Math.min(x, width - w))
    const top = Math.max(0, Math.min(y, height - boxH))
    ctx.fillStyle = gridPillColor()
    ctx.beginPath()
    ctx.roundRect(left, top, w, boxH, 3 * k)
    ctx.fill()
    ctx.fillStyle = GRID_TEXT_COLOR
    ctx.fillText(text, left + pad, top + (boxH - font) / 2)
  }

  // Подписи по всем четырём краям: судоводитель читает ту линейку, что ближе.
  // Подписываются только помеченные деления — на густой сетке остальные подписи
  // наехали бы друг на друга (прореживание считает graticule.ts).
  for (const t of grat.lon) {
    if (!t.labelled) continue
    const x = t.px * k + GRID_LABEL_OFFSET * k
    pill(t.label, x, gap)
    pill(t.label, x, height - gap - boxH)
  }
  for (const t of grat.lat) {
    if (!t.labelled) continue
    const y = t.px * k + gap
    pill(t.label, gap, y)
    pill(t.label, width - gap - (ctx.measureText(t.label).width + pad * 2), y)
  }
  ctx.restore()
}

/** Рисует плашку в правом нижнем углу готового кадра. */
export function drawPlate(ctx: CanvasRenderingContext2D, info: ExportContext,
                          width: number, height: number): void {
  const lines = exportPlateLines(info)
  const header = plateHeaderLines(info)
  ctx.save()
  ctx.font = PLATE_FONT
  ctx.textBaseline = 'top'
  // Ширину меряем шрифтом, а не считаем по «средней ширине символа»: кириллица
  // в моноширинном шрифте шире оценки, и текст упирался в край плашки.
  // Метка съедает поле слева — ширину считаем с её учётом, иначе строки
  // легенды упираются в правый край.
  const measured = lines.reduce(
    (m, l) => Math.max(m, ctx.measureText(l.text).width + (l.mark ? MARK_W : 0)),
    0,
  )
  const box = plateBox(measured, lines.length)
  const x = width - box.width - 12
  const y = height - box.height - 12
  // Подложка полупрозрачная, но тёмная: плашка ложится и на светлую сушу, и на
  // тёмное море, и текст обязан читаться на обоих.
  ctx.fillStyle = withAlpha(mapColorOf('export-plate'), 0.82)
  ctx.strokeStyle = 'rgba(255,255,255,0.18)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.roundRect(x, y, box.width, box.height, 8)
  ctx.fill()
  ctx.stroke()

  lines.forEach((line, i) => {
    const top = y + PLATE_PAD + i * PLATE_LINE_H
    if (line.mark) drawMark(ctx, line.mark, x + PLATE_PAD, top + 6)
    // Шапка (адрес + бренд) ярче остального — цвет решает exportPlate.
    ctx.fillStyle = plateLineColor(i, header)
    ctx.fillText(line.text, x + PLATE_PAD + (line.mark ? MARK_W : 0), top)
  })
  ctx.restore()
}

/** Таймер ролика: крупная дата кадра и полоска записанного окна под ней.
 *
 *  Заведён потому, что ролик перестал стоять на одной дате: он идёт по суткам, и
 *  без даты в кадре зритель не знает, что видит, — а мелкая строка в угловой
 *  плашке для этого не годится, её на телефоне не поймать.
 *
 *  Метка на полоске отделяет факт от прогноза. Её отсутствие — тоже сообщение:
 *  сегодня вне записанного окна (см. `timerBarMarks`). */
export function drawTimer(
  ctx: CanvasRenderingContext2D,
  frame: TimerFrame,
  width: number,
  height: number,
): void {
  const label = timerLabel(frame)
  ctx.save()
  ctx.font = TIMER_FONT
  ctx.textBaseline = 'top'
  const box = timerLayout(width, height, ctx.measureText(label).width)
  ctx.fillStyle = withAlpha(mapColorOf('export-plate'), 0.82)
  ctx.strokeStyle = 'rgba(255,255,255,0.18)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.roundRect(box.x, box.y, box.width, box.height, 10)
  ctx.fill()
  ctx.stroke()

  ctx.fillStyle = '#ffffff'
  ctx.textAlign = 'center'
  ctx.fillText(label, box.x + box.width / 2, box.y + TIMER_PAD)

  // Полоска окна: жёлоб, пройденная часть, бегунок и метка «сегодня».
  const barX = box.x + TIMER_PAD
  const barW = box.width - TIMER_PAD * 2
  const barY = box.y + TIMER_PAD + TIMER_LINE_H
  const marks = timerBarMarks(frame)
  ctx.fillStyle = 'rgba(255,255,255,0.22)'
  ctx.fillRect(barX, barY, barW, TIMER_BAR_H)
  ctx.fillStyle = 'rgba(255,255,255,0.72)'
  ctx.fillRect(barX, barY, barW * marks.head, TIMER_BAR_H)
  if (marks.today != null) {
    // Белые обводки и метки читаемости — общие на все бренды (см. mapColors).
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(barX + barW * marks.today - 1, barY - 3, 2, TIMER_BAR_H + 6)
  }
  ctx.restore()
}

/** Кадр карты + сетка + плашка → canvas. `scale` уменьшает кадр (для видео),
 *  `out` — полотно для переиспользования: в записи их полторы сотни подряд, и на
 *  каждый кадр заводить своё — лишний мусор.
 *
 *  Порядок слоёв тот же, что на экране: сетка поверх карты, плашка поверх
 *  сетки — иначе линия перечеркнула бы легенду. Таймер идёт последним: он
 *  принадлежит ролику, а не карте, и заслонять его нечему. */
export function composeFrame(
  source: HTMLCanvasElement,
  info: ExportContext,
  scale: number,
  grat: GraticuleFrame | null = null,
  out: HTMLCanvasElement = document.createElement('canvas'),
  /** Таймер кадра. `null` — снимок PNG: там дата одна и стоит в плашке. */
  timer: TimerFrame | null = null,
): HTMLCanvasElement {
  out.width = Math.round(source.width * scale)
  out.height = Math.round(source.height * scale)
  const ctx = out.getContext('2d')
  if (!ctx) return out
  ctx.drawImage(source, 0, 0, out.width, out.height)
  drawGraticule(ctx, grat, out.width, out.height)
  // Подпись подложки — ПЕРЕД плашкой: плашка кладётся в правый низ, подпись в
  // левый, но порядок фиксируем на случай узкого кадра, где они сойдутся.
  // Кадр уходит из кабинета наружу, и условия обоих поставщиков требуют, чтобы
  // их картография была подписана (HTML-оверлей сюда не попадает никак).
  drawAttribution(ctx, activeBasemap(), out.width, out.height)
  drawPlate(ctx, info, out.width, out.height)
  if (timer) drawTimer(ctx, timer, out.width, out.height)
  return ctx.canvas
}

/** Пиксели готового кадра. */
export function framePixels(frame: HTMLCanvasElement): Uint8ClampedArray | null {
  const ctx = frame.getContext('2d')
  if (!ctx) return null
  return ctx.getImageData(0, 0, frame.width, frame.height).data
}

/** Отличаются ли два кадра хоть одним пикселем.
 *
 *  Выходим на первом же отличии: когда движение есть, ответ приходит мгновенно,
 *  а полный проход платится только в том случае, ради которого проверка и
 *  заведена, — в мёртвой записи.
 */
export function pixelsDiffer(a: Uint8ClampedArray, b: Uint8ClampedArray): boolean {
  if (a.length !== b.length) return true
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return true
  }
  return false
}

export function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  // Освобождаем в следующем такте: Safari не успевает начать скачивание, если
  // ссылку отозвать сразу.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** Байты PNG текущего кадра — с координатной сеткой и плашкой.
 *
 *  Отдельно от `exportPng`, потому что у кадра две дороги: одиночный снимок
 *  уходит человеку сразу, а кадр серии — в архив вместе с четырьмя другими и
 *  файлом параметров. Собирается он при этом одинаково, и второй сборки быть не
 *  должно: плашка на кадре серии обязана выглядеть ровно как на одиночном. */
export async function framePngBytes(
  map: HTMLCanvasElement,
  info: ExportContext,
  grat: GraticuleFrame | null = null,
): Promise<Uint8Array<ArrayBuffer>> {
  // Знак подложки декодируется ЗАРАНЕЕ: `composeFrame` синхронен, а ролик
  // зовёт его на каждый кадр — ждать там нечего и некогда.
  await prepareAttribution(activeBasemap(), getLang())
  const frame = composeFrame(map, info, 1, grat)
  const blob = await new Promise<Blob | null>(res => frame.toBlob(res, 'image/png'))
  if (!blob) throw new Error('exportPng: browser returned no frame')
  return new Uint8Array(await blob.arrayBuffer())
}

/** PNG текущего кадра с координатной сеткой и плашкой. */
export async function exportPng(
  map: HTMLCanvasElement,
  info: ExportContext,
  fileName: string,
  grat: GraticuleFrame | null = null,
): Promise<void> {
  const bytes = await framePngBytes(map, info, grat)
  download(new Blob([bytes], { type: 'image/png' }), fileName)
}
